import { getSafeInternalRedirectPath } from '@helpers/getSafeInternalRedirectPath';
import { parseUserAgent } from '@helpers/parseUserAgent';
import { savePendingLogoutRedirect } from '@helpers/savePendingLogoutRedirect';
import { useAPI } from '@providers/useAPI';
import { useQueryClient } from '@tanstack/react-query';

import { AuthService, NotificationsService, OpenAPI } from 'app/API';
import { getRefreshPromise } from 'app/http';
import { broadcastLogout, isLogoutStorageEvent } from 'app/http/authLogoutBroadcast';
import { handleUnhandledApiCallRejection } from 'app/http/handleUnhandledApiCallRejection';
import {
  invalidateSession,
  registerSessionInvalidator,
  unregisterSessionInvalidator,
} from 'app/http/sessionInvalidation';
import { createContext, ReactNode, useContext, useEffect, useRef, useState } from 'react';

type TState = {
  isAuthenticated: boolean;
  authenticate: (redirectPath?: string) => void;
  isInitialized: boolean;
  isLoading: boolean;
  logout: (redirectPath?: string) => Promise<void>;
};

const InitContext = createContext<TState>({
  isAuthenticated: false,
  authenticate: () => null,
  isInitialized: false,
  isLoading: false,
  logout: async () => {},
});

const isSignInRequest = (url: string) => {
  const path = url.split('?')[0].replace(/\/+$/, '');

  return path.endsWith('/auth/login') || path.endsWith('/auth/sso/login');
};

type TProps = {
  children: ReactNode;
};

export const InitProvider = ({ children }: TProps) => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const isAuthenticatedRef = useRef(isAuthenticated);
  isAuthenticatedRef.current = isAuthenticated;

  useEffect(() => {
    if (isLoading || isInitialized) {
      return;
    }
    setIsLoading(true);
    const initialize = async () => {
      try {
        const id = await call(() => AuthService.getUserId(), { dontCatchErrors: true });
        if (id && typeof id === 'string') {
          const refreshResult = await getRefreshPromise();
          setIsAuthenticated(refreshResult.kind !== 'auth-failure');
        } else {
          setIsAuthenticated(false);
        }
      } catch {
        setIsAuthenticated(false);
      } finally {
        setIsInitialized(true);
        setIsLoading(false);
      }
    };
    void initialize();
  }, [call, isInitialized, isLoading]);

  useEffect(() => {
    const invalidator = () => {
      if (!isAuthenticatedRef.current) {
        return false;
      }
      void queryClient.cancelQueries();
      queryClient.clear();
      setIsAuthenticated(false);
      return true;
    };

    registerSessionInvalidator(invalidator);
    return () => unregisterSessionInvalidator(invalidator);
  }, [queryClient]);

  useEffect(() => {
    const responseHandler = (response: XMLHttpRequest) => {
      if (response.status === 401 && isAuthenticatedRef.current && !isSignInRequest(response.responseURL || '')) {
        invalidateSession({ broadcast: true });
      }
      return response;
    };

    OpenAPI.interceptors.response.use(responseHandler);
    return () => OpenAPI.interceptors.response.eject(responseHandler);
  }, []);

  useEffect(() => {
    const storageHandler = (event: StorageEvent) => {
      if (isLogoutStorageEvent(event)) {
        invalidateSession({ broadcast: false });
      }
    };

    window.addEventListener('storage', storageHandler);
    return () => window.removeEventListener('storage', storageHandler);
  }, []);

  useEffect(() => {
    window.addEventListener('unhandledrejection', handleUnhandledApiCallRejection);
    return () => window.removeEventListener('unhandledrejection', handleUnhandledApiCallRejection);
  }, []);

  const logout = async (redirectPath?: string) => {
    const safeRedirectPath = typeof redirectPath === 'string' ? getSafeInternalRedirectPath(redirectPath) : undefined;
    let providerLogoutUrl = '';

    try {
      const browserData = parseUserAgent();
      await call(() =>
        NotificationsService.unsubscribe({
          requestBody: {
            browserName: String(browserData.browser?.name),
            browserVersion: String(browserData.browser?.version),
            osName: String(browserData.os?.name),
            deviceName: String(browserData.device?.model),
            userAgent: navigator.userAgent,
          },
        }),
      );
    } catch (error) {
      console.error('Unable to unsubscribe from notifications during logout', error);
    }

    try {
      const logoutResponse = await call(() => AuthService.logout());
      if (typeof logoutResponse?.logoutUrl === 'string') {
        providerLogoutUrl = logoutResponse.logoutUrl;
      }
    } catch (error) {
      console.error('Unable to complete logout request', error);
    }

    savePendingLogoutRedirect(providerLogoutUrl ? safeRedirectPath ?? null : null);

    document.cookie = 'ftk=; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    broadcastLogout();
    window.location.assign(providerLogoutUrl || safeRedirectPath || '/');
  };

  const authenticate = (redirectPath = '/dashboard') => {
    window.location.assign(redirectPath);
  };

  return (
    <InitContext.Provider
      value={{
        isAuthenticated,
        authenticate,
        isLoading,
        isInitialized,
        logout,
      }}
    >
      {children}
    </InitContext.Provider>
  );
};

export const useInitContext = () => useContext(InitContext);
